import { FormField } from "@shared/modules/utils";
import EventService from "@shared/services/EventService";
import React, { useEffect, useState, useRef, cloneElement, useMemo } from "react";

/**
 * Framework requirements,
 * 1] Auto-focus fields across the screen
 * 2] Access to inputRefs for controlled focusing
 * 3] Dynamic focus behavior based on field type
 * 4] Configurable handling from parent
 */

const FOCUS_DELAY_CONFIG = {
    text: 150,
    number: 150,
    select: 400,
    radio: 100,
    card: 100,
    default: 100
};

const FormGroupWrapper = ({ children, formGroupWrapperRefs = [], onFocusChange = () => {}, config = {} }) => {
    const [activeFormFieldIndex, setActiveFormFieldIndex] = useState(0);
    const lastFocusedIndex = useRef(null);

    const didAutoFocus = useRef(false);

    useEffect(() => {
        if (!didAutoFocus.current && formGroupWrapperRefs.length > 0) {
            didAutoFocus.current = true;
            setActiveFormFieldIndex(0);
        }
    }, [formGroupWrapperRefs]);

    useEffect(() => {
        const currentField = formGroupWrapperRefs[activeFormFieldIndex];

        if (currentField && lastFocusedIndex.current !== activeFormFieldIndex) {
            lastFocusedIndex.current = activeFormFieldIndex;
            focusActiveField(currentField);
            onFocusChange(currentField);
        }
    }, [activeFormFieldIndex, formGroupWrapperRefs]);

    useEffect(() => {
        const handleKeypadBlur = shouldFocusNext => {
            console.log(`[Event] keypad.blur received with shouldFocusNext = ${shouldFocusNext}`);
            const currentField = formGroupWrapperRefs[activeFormFieldIndex];
            //Select fields auto-focus do not get called on keypad blur, part of mergedOnChange
            if (shouldFocusNext && (currentField?.type !== "select" || currentField?.type !== "select2")) {
                focusNextField();
            }
        };

        const keyboardBlurEvent = EventService.subscribe("keypad.blur", handleKeypadBlur);
        // console.log("[Subscribe] Subscribed to keypad.blur");

        return () => {
            // console.log("[Cleanup] Unsubscribing from keypad.blur");
            keyboardBlurEvent.remove();
        };
    }, [activeFormFieldIndex]);

    const focusActiveField = field => {
        const { inputRef, type } = field;
        const delay = field.delay ?? config?.[type]?.delay ?? FOCUS_DELAY_CONFIG[type] ?? FOCUS_DELAY_CONFIG.default;

        console.log(`[Focus] Preparing to focus type="${type}" with delay=${delay}ms`);

        setTimeout(() => {
            if (document.activeElement === inputRef?.current) return;
            inputRef?.current?.focus?.();
        }, delay);
    };

    const focusNextField = () => {
        if (formGroupWrapperRefs.length === 0 || activeFormFieldIndex >= formGroupWrapperRefs.length) {
            // console.log("[FocusNext] active field was last field or invalid index — stopping focus.");
            return;
        }

        let nextFocusFormFieldIndex = getNextFocusFormField(activeFormFieldIndex);

        // console.log(`[FocusNext] Moving focus to next field: ${nextFocusFormFieldIndex}`);
        setActiveFormFieldIndex(nextFocusFormFieldIndex);
    };

    const getNextFocusFormField = currentFormFieldIndex => {
        let index = currentFormFieldIndex + 1;

        while (index < formGroupWrapperRefs.length) {
            const next = formGroupWrapperRefs[index];
            const nextVal = next?.inputRef?.current?.val();
            const nextValid = next?.inputRef?.current?.isValid(true);

            // Check if next field has value and is valid
            // auto focus only when user has not entered value or is not a valid value
            if (!nextVal || !nextValid) {
                return index;
            }
            index++;
        }

        return -1; // no next focusable field
    };

    const validChildren = React.Children.toArray(children).filter(Boolean);

    const enhanceChildren = (children, refList, refIndex = { current: -1 }) => {
        return React.Children.map(children, child => {
            if (!React.isValidElement(child)) {
                // console.log("[enhanceChildren] Skipping invalid child:", child);
                return child;
            }

            // console.log("[enhanceChildren] Processing child of type:", child.type?.name || child.type);

            if (child.type === FormField) {
                refIndex.current++;
                const index = refIndex.current;
                const refEntry = refList[index];
                const { type } = refEntry || {};
                const existingOnChange = child.props.onChange;

                // console.log(`[enhanceChildren] Found FormField at index ${index}, type="${type}"`);

                const originalVal = refEntry?.val;
                if (originalVal && typeof originalVal === "function") {
                    refEntry.val = newVal => {
                        if (newVal === undefined) {
                            return originalVal();
                        }

                        // Customize behavior based on type
                        if (type === "select" || type === "select2") {
                            console.log(`[.val override][Select] Setting to:`, newVal);
                            focusNextField?.();
                        }

                        return originalVal(newVal);
                    };
                }

                const mergedOnChange = newVal => {
                    // console.log(`[onChange] Triggered on FormField at index ${index}, type="${type}"`);
                    existingOnChange?.(newVal);

                    if (type === "select" || type === "select2") {
                        // const currentField = refList[index];
                        // const isValid = currentField?.isValid?.();
                        // const value = e;
                        const value = newVal;

                        //// console.log(`[onChange][Select] Value:`, value, `IsValid:`, isValid);
                        console.log(`[onChange][Select] Value:`, value);

                        if (value !== undefined && value !== null && value !== "") {
                            // console.log(`[onChange][Select] Valid select input — focusing next field`);
                            focusNextField();
                        } else {
                            // console.log(`[onChange][Select] Invalid or empty select input — not moving focus`);
                        }
                    }
                };

                return cloneElement(child, {
                    key: child.key ?? index,
                    onChange: mergedOnChange
                });
            }

            if (child.props?.children) {
                // console.log("[enhanceChildren] Recursing into nested children");
                return cloneElement(child, {
                    children: enhanceChildren(child.props.children, refList, refIndex)
                });
            }

            // console.log("[enhanceChildren] Returning non-FormField child as-is");
            return child;
        });
    };

    const enhancedChildren = useMemo(
        () => enhanceChildren(validChildren, formGroupWrapperRefs),
        [validChildren, formGroupWrapperRefs]
    );

    return <>{enhancedChildren}</>;
};

export default FormGroupWrapper;
